import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy} from "@angular/core";
import {Subscription, filter} from "rxjs";
import {HomeInspireService, IInspireVideo} from "./home-inspire.service";

/**
 * One reusable first-frame thumbnail, used by BOTH the home strip and the
 * dialog filmstrip so they share code AND the same cache.
 *
 * It is pure <img>/icon — it NEVER loads a <video> itself. On a cache miss
 * it asks HomeInspireService to capture the frame (priority, since it's on
 * screen) and subscribes for the result, then swaps the industry icon out
 * for the cached <img>. This keeps the DOM cheap no matter how many thumbs
 * are mounted (the whole strip + filmstrip at once) — the actual decoding
 * is throttled centrally by the service.
 *
 * Sizing is driven by the `--itmb-icon-size` CSS var the parent sets.
 */
@Component({
    selector: "app-inspire-thumb",
    templateUrl: "./home-inspire-thumb.component.html",
    styleUrls: ["./home-inspire-thumb.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HomeInspireThumbComponent implements OnDestroy {
    private _video!: IInspireVideo;
    private sub?: Subscription;

    /** The clip to show. Setter resets per-clip state, reads the cache up
     *  front, and (on a miss) requests a priority capture + waits for it. */
    @Input({required: true})
    set video(v: IInspireVideo) {
        this._video = v;
        this.sub?.unsubscribe();
        this.cachedUrl = this.inspireService.getThumb(v.file) || null;
        if (!this.cachedUrl) {
            // It's on screen now — capture it ahead of the idle backlog.
            this.inspireService.ensureThumb(v.file, true);
            this.sub = this.inspireService.thumbCaptured$
                .pipe(filter((file) => file === v.file))
                .subscribe(() => {
                    this.cachedUrl = this.inspireService.getThumb(v.file) || null;
                    this.cdr.markForCheck();
                });
        }
    }
    get video(): IInspireVideo {
        return this._video;
    }

    cachedUrl: string | null = null;

    constructor(
        private inspireService: HomeInspireService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }
}
